import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  inject,
} from '@angular/core';
import { AccessControlService, SessionService } from '@bizzdesign/auth';
import { SplitSearchContainerComponent } from '@bizzdesign/feature-search';
import { TrialPopoverStore } from '@bizzdesign/feature-trial-experience';
import { FeatureFlagsStore } from '@bizzdesign/shared/feature-flags';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { type BDMenuItem } from '@bizzdesign/shared/models';
import { ProductAnalyticsService } from '@bizzdesign/shared/services';
import {
  BdAvatar,
  UNIFY_MANAGEMENT_URL,
  USERFLOW_CONFIG,
  type UserflowConfig,
} from '@bizzdesign/shared/utils';
import { ClippyStore } from '@bizzdesign/ui-components';
import { type Observable, map, tap } from 'rxjs';
import { type ILogObj, Logger } from 'tslog';
import userflow from 'userflow.js';
import { type HeaderConfig } from '../../utils/layout-config.utils';
import { createSupportMenuItems } from '../../utils/support-menu-items.utils';
import { createUnifyManagementMenuItem } from '../../utils/unify-management-menu-item.utils';
import { AccountToolbarComponent } from '../account-toolbar/account-toolbar.component';
import { DynamicToolbarComponent } from '../dynamic-toolbar/dynamic-toolbar.component';
import { NavigationToolbarComponent } from '../navigation-toolbar/navigation-toolbar.component';

@Component({
  selector: 'bd-header',
  imports: [
    NgClass,
    AsyncPipe,
    NavigationToolbarComponent,
    AccountToolbarComponent,
    DynamicToolbarComponent,
    SplitSearchContainerComponent,
    SharedI18nModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.bd-trial-popover-open]': 'trialPopoverStore.popoverOpen()' },
})
export class HeaderComponent {
  logger: Logger<ILogObj> | null = null;
  readonly trialPopoverStore = inject(TrialPopoverStore);
  readonly featureFlags = inject(FeatureFlagsStore);
  readonly clippyStore = inject(ClippyStore);
  private readonly sessionService = inject(SessionService);
  private readonly productAnalyticsService = inject(ProductAnalyticsService);
  private readonly userflowConfig = inject<UserflowConfig>(USERFLOW_CONFIG);
  private readonly accessControlService = inject(AccessControlService);
  private readonly unifyManagementUrl = inject(UNIFY_MANAGEMENT_URL);

  @Input({ required: true }) headerConfig!: HeaderConfig;

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    if (this.trialPopoverStore.popoverOpen()) {
      this.trialPopoverStore.setPopoverOpen(false);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: Event): void {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }

    const editableTarget =
      event.target instanceof Element &&
      event.target.closest(
        'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]',
      ) !== null;
    if (event.isComposing || editableTarget || event.repeat) {
      return;
    }

    // Alt+C on Windows/Linux, Option+C on macOS. Matching on `code` (the
    // physical key) rather than `key` is required because macOS turns
    // Option+C into the "ç" character, so `event.key` would not be "c".
    const isClippyShortcut =
      event.altKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey &&
      event.code === 'KeyC';
    if (!isClippyShortcut || !this.featureFlags.devClippyEnabled()) {
      return;
    }
    event.preventDefault();
    this.clippyStore.toggle();
  }

  userAvatar$: Observable<BdAvatar | null>;
  accountMenuItems$: Observable<BDMenuItem[]> = this.accessControlService
    .getActionState$('unifyManagement', 'open')
    .pipe(
      map((actionState) =>
        this.buildAccountMenuItems(actionState === 'accessible'),
      ),
    );

  private buildAccountMenuItems(
    canAccessUnifyManagement: boolean,
  ): BDMenuItem[] {
    // Some environments (Orion, e2e, usertest, per-tenant SaaS) have no
    // Unify Management deployment, so unifyManagementUrl is '' there — hide
    // the command rather than opening a blank tab.
    const showUnifyManagement =
      canAccessUnifyManagement && this.unifyManagementUrl !== '';
    return [
      // AccountMenuComponent only prepends this once, in ngOnInit, so each
      // freshly built array here must already include it to survive re-emission.
      { id: 'userAccountMenuItem' },
      {
        separator: true,
      },
      ...createSupportMenuItems(this.productAnalyticsService),
      {
        separator: true,
      },
      ...(showUnifyManagement
        ? [
            createUnifyManagementMenuItem(
              this.unifyManagementUrl,
              this.productAnalyticsService,
            ),
            { separator: true },
          ]
        : []),
      {
        id: 'log-out',
        labelKey: 'header.logOut',
        tScope: 'featureLayout',
        command: () => {
          this.productAnalyticsService.reset();
          this.sessionService.logout();
        },
      },
    ];
  }

  constructor() {
    this.logger = new Logger({
      overwrite: {
        transportFormatted: (
          logMetaMarkup: string,
          logArgs: unknown[],
          logErrors: string[],
        ): void => {
          console.log(...logArgs);
        },
      },
    });

    if (this.userflowConfig && this.userflowConfig.userflowKey !== '') {
      try {
        userflow.init(this.userflowConfig.userflowKey);
      } catch (error) {
        this.logger?.warn('Failed to initialize Userflow:', error);
      }
    }
    this.userAvatar$ = this.sessionService.getAuthenticatedUser().pipe(
      tap(this.identifyUserWithUserflow.bind(this)),
      map((authenticatedUser) =>
        authenticatedUser ? new BdAvatar(authenticatedUser) : null,
      ),
    );
  }

  private async identifyUserWithUserflow(
    authenticatedUser: any,
  ): Promise<void> {
    if (
      this.userflowConfig &&
      this.userflowConfig.userflowKey !== '' &&
      authenticatedUser
    ) {
      try {
        await userflow.identify(authenticatedUser.userId, {
          name: `${authenticatedUser.firstName} ${authenticatedUser.lastName}`,
          firstName: authenticatedUser.firstName,
          lastName: authenticatedUser.lastName,
          email: authenticatedUser.email,
          customer_name: this.userflowConfig.customerName,
          ...authenticatedUser.roles,
        });
      } catch (error) {
        this.logger?.warn('Failed to identify user with Userflow:', error);
      }
    }
  }
}
